import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectorRef, HostListener, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  ChartConfiguration,
  ChartType,
  ChartOptions,
  ChartData,
} from 'chart.js';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,

} from '@angular/forms';
import { NgChartsModule, BaseChartDirective } from 'ng2-charts';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { AuthService } from '../../shared/services';
import { environment } from '../../../../environments/environment';
import { PieChartComponent } from '../../shared/reusables/pie-chart/pie-chart.component';
import { StatisticCardsComponent } from '../../shared/reusables/statistic-cards/statistic-cards.component';
import { AlertComponent } from '../../shared/reusables/alert/alert.component';
import { RouterModule, Router } from '@angular/router';
import { TooltipComponent } from '../../shared/tooltip/tooltip.component';
import { jwtDecode } from 'jwt-decode';
import { BudgetService, BudgetAssignment } from '../../../shared/services/budget.service';
import { BudgetSelectorComponent } from './budget-selector.component';
import { InventoryService } from '../../../shared/services/inventory.service';
import { FilterPipe } from '../../../shared/pipes/filter.pipe';
import { ItemStandardCostHistoryService, ItemStandardCostHistoryCreateRequest } from '../../../shared/services/item-standard-cost-history.service';
import { MobileManufacturingViewComponent } from './mobile-manufacturing-view/mobile-manufacturing-view.component';
import { BomFormPayload } from './mobile-manufacturing-view/bom-item-offcanvas/bom-item-offcanvas.component';
import { FactoryOrderFormPayload } from './mobile-manufacturing-view/factory-order-offcanvas/factory-order-offcanvas.component';

export interface InventoryTransactionDTO {
  transactionID?: number;
  itemID: number;
  warehouseID: number;
  transactionType: string;
  quantityChange: number;
  lowStockThreshold: number;
  costPerUnit?: number;
  transactionDate: Date;
  referenceType?: string;
  referenceID?: string;
  userID: number;
  notes?: string;
  batchNumber?: string | null;
  expiryDate?: Date | null;
  referenceTypeCode?: string;
}

interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  description?: string;
  itemTypeID: number;
  baseUOMID: number;
  purchaseUOMID?: number;
  lowStockThreshold?: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  targetSellingPrice?: number;
  targetProfitMarginPercent?: number;
  targetCost?: number;
  valuationMethodCode?: string;
  categoryID: number;
}
interface BOMHeaders {
  bomHeaderID: number;
  finishedGoodItemID: number;
  bomVersion: string;
  description: string;
  isActive: boolean;
  validFrom: string;
  validTo: string;
  createdAt: string;
  userIDCreated: number;
  finishedGoodItem: any;
  createdByUser: any;
  components?: BOMComponents[];
  expanded?: boolean;
}
interface UOM {
  uomid: number;
  uomCode: string;
  uomName: string;
}
interface BOMComponents {
  bomComponentID: number;
  bomHeaderID: number;
  rawMaterialItemID: number;
  quantityRequired: number;
  componentUOMID: number;
  scrapFactor: number;
  notes: string;
}
interface GroupedBillOfMaterial {
  createdAt: string;
  productId: number;
  productName: string;
  expanded?: boolean;
  rawMaterials: {
    rawMaterialId: number;
    rawMaterialName: string;
    quantityRequired: number;
    createdAt: string;
  }[];
}
interface ProductionOrderStatusHistory {
  statusHistoryID: number;
  productionOrderID: number;
  status: 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold';
  statusChangedAt: string;
  userIDChanged: number;
  notes: string;
}
interface ProductionOrders {
  productionOrderID: number;
  orderNumber: string;
  finishedGoodItemID: number;
  quantityToProduce: number;
  quantitySold?: number; // Track sold quantity
  uOMID: number;
  bOMHeaderIDUsed: number;
  status: 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold' | 'Partially Sold';
  warehouseID: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  actualDirectLabourHours?: number;
  actualMachineHours?: number;
  createdAt: string;
  notes?: string;
  userIDCreated: number;
  completionPercentage?: number;
}
interface ProductionOrdersExtended extends ProductionOrders {
  productName: string;
}
interface UserDTO {
  userId: number;
  username: string;
  email?: string;
  role?: string;
  passwordHash?: string;
  createdAt: string;
}

interface Budget {
  budgetID: string;
  budgetName: string;
}

@Component({
  selector: 'app-manufacturing-supply-chain-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NgChartsModule,
    DragDropModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PieChartComponent,
    TooltipComponent,
    BudgetSelectorComponent,
    FilterPipe,
    MobileManufacturingViewComponent,
  ],
  templateUrl: './manufacturing-supply-chain-dashboard.component.html',
  styleUrls: ['./manufacturing-supply-chain-dashboard.component.css'],
})
export class ManufacturingSupplyChainDashboardComponent implements OnInit {
  expandedDescriptions: { [bomHeaderID: number]: boolean } = {};
  inventoryTransactions: any[] = [];
  userRole: string | null = null;
  isTablesStacked: boolean = false;
  productsWithoutBOMs: ItemDTO[] =[];
  tenantName: any;
  fullName: any;
   isDescriptionTruncated(description: string, limit = 35): boolean {
    return !!description && description.length > limit;
  }

  // Helper to display selected raw material name
  getRawMaterialDisplay(itemId: number | null | undefined): string {
    if (!itemId) return '';
    const material = this.rawMaterials.find(rm => rm.itemID === itemId);
    if (!material) return '';
    const category = this.getCategoryDisplayName(material.categoryID);
    return `${material.itemName} - ${category}`;
  }

  // Display the selected component UOM code/name; fallback to raw material base/purchase UOM
  getComponentUOMCode(component: { componentUOMID?: number | null, rawMaterialItemID?: number | null }): string {
    const selectedUomId = component?.componentUOMID;
    if (selectedUomId) {
      const selected = this.uoms.find(u => u.uomid === selectedUomId);
      if (selected) return selected.uomCode || selected.uomName || '';
    }

    const rawMaterialId = component?.rawMaterialItemID;
    if (!rawMaterialId) return '';
    const material = this.rawMaterials.find(rm => rm.itemID === rawMaterialId);
    if (!material) return '';
    const uomId = (material.baseUOMID ?? material.purchaseUOMID) as number | undefined;
    if (uomId === undefined || uomId === null) return '';
    const uom = this.uoms.find(u => u.uomid === uomId);
    return uom?.uomCode || uom?.uomName || '';
  }

  // Open/close the custom dropdown for a specific component row
  toggleRawMaterialDropdown(index: number, triggerEl?: HTMLElement): void {
    const isOpening = this.rawMaterialDropdownOpenIndex !== index;
    this.rawMaterialDropdownOpenIndex = isOpening ? index : null;
    if (isOpening && triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      this.rawMaterialDropdownStyles[index] = {
        top: `${Math.round(rect.bottom + 4)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
      };
      // Set guard to skip the immediate outside-click close
      this.rawMaterialDropdownJustOpened = true;
      setTimeout(() => (this.rawMaterialDropdownJustOpened = false), 0);
    }
  }

  // Optional: use mousedown to open before click bubbling
  onRawMaterialToggleMouseDown(index: number, triggerEl: HTMLElement, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleRawMaterialDropdown(index, triggerEl);
  }

  // When user selects a raw material from custom dropdown, set form control and close menu
  selectRawMaterial(index: number, material: any): void {
    const control = this.components?.at(index)?.get('rawMaterialItemID');
    control?.setValue(material.itemID);
    // Trigger any existing change logic
    this.onRawMaterialChange(index);
    // Close dropdown and clear search text for better UX
    this.rawMaterialDropdownOpenIndex = null;
    this.rawMaterialSearchText = '';
  }

  // Close dropdown on outside click
  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent) {
    if (this.rawMaterialDropdownJustOpened) {
      // Skip closing on the very click that opened the dropdown
      this.rawMaterialDropdownJustOpened = false;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.rawMaterialDropdownOpenIndex = null;
      this.bomProductDropdownOpen = false;
      return;
    }
    // Raw material dropdown handling
    const openIndex = this.rawMaterialDropdownOpenIndex;
    if (openIndex !== null && openIndex !== undefined) {
      const btn = document.getElementById(`rawMaterialBtn_${openIndex}`);
      const menu = document.getElementById(`rawMaterialMenu_${openIndex}`);
      if (btn?.contains(target) || menu?.contains(target)) {
        // Click happened inside the dropdown or its button; do not close
        return;
      }
      this.rawMaterialDropdownOpenIndex = null;
    }

    // BOM product dropdown handling
    const bomBtn = document.getElementById('bomProductBtn');
    const bomMenu = document.getElementById('bomProductMenu');
    if (bomBtn?.contains(target) || bomMenu?.contains(target)) {
      return;
    }
    this.bomProductDropdownOpen = false;
  }

  // Recompute/close on resize and scroll to avoid misplacement
  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange() {
    // Close to keep it simple; could recompute position if desired
    this.rawMaterialDropdownOpenIndex = null;
    this.bomProductDropdownOpen = false;
  }
  getTruncatedDescription(description: string, limit = 35): string {
    if (!description) return '';
    return description.length > limit ? description.substring(0, limit) + '...' : description;
  }
  toggleDescriptionExpansion(bomHeaderID: number): void {
    this.expandedDescriptions[bomHeaderID] = !this.expandedDescriptions[bomHeaderID];
  }
  toggleTableLayout(): void {
    this.isTablesStacked = !this.isTablesStacked;
  }
  toggleBomActiveStatus(bom: BOMHeaders): void {
    const newStatus = !bom.isActive;
    bom.isActive = newStatus;

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http.put(
      `${environment.apiUrl}/api/BOMHeaders/Update/?BOMHeadersId=${bom.bomHeaderID}`,
      { ...bom, isActive: newStatus },
      { headers }
    ).subscribe({
      next: (updatedBom) => {
        const index = this.billOfMaterials.findIndex(b => b.bomHeaderID === bom.bomHeaderID);
        if (index !== -1) {
          this.billOfMaterials[index] = { ...this.billOfMaterials[index], ...(updatedBom as BOMHeaders) };
        }
      },
      error: (error) => {
// $&
        bom.isActive = !newStatus;
        this.alertMessage = 'Failed to update BOM status. Please try again.';
        this.alertType = 'error';
        this.showAlert = true;
      }
    });
  }

  isDescriptionExpanded(bomHeaderID: number): boolean {
    return !!this.expandedDescriptions[bomHeaderID];
  }
  showSellModal: boolean = false;
  order: ProductionOrders | null = null;
  quantityToSell: number = 0;
  isEditMode: boolean = false;
  editingBomHeaderID: number | null = null;
  donutData!: ChartData<'doughnut'>;
  donutOptions!: ChartOptions<'doughnut'>;
  barData!: ChartData<'bar'>;
  barOptions!: ChartOptions<'bar'>;
  animateClickRaw = false;
  showAddModal = false;
  showCompletedOrders = false;
  showSoldOrders = false;
  showInactiveBOMs = false;
  stockCheckResults: any[] = [];
  // When true, disable cdkDrag on cards to allow the slider to be dragged smoothly
  isAdjustingSlider: boolean = false;
  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  @HostListener('document:touchcancel', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onGlobalPointerRelease(event: Event) {
    if (this.isAdjustingSlider) {
      this.isAdjustingSlider = false;
    }
  }
  get displayedFactoryOrders() {
    const filteredOrders = this.factoryOrders.filter(order =>
      order.status !== 'Sold' && order.status !== 'Partially Sold'
    );

    return this.showCompletedOrders
      ? [...filteredOrders]
      : filteredOrders.filter(order => order.status !== 'Completed');
  }
  get completedFactoryOrders() {
    // Filter orders to show only completed, partially sold, and optionally sold orders
    let filteredOrders = this.factoryOrders.filter(order =>
      order.status === 'Completed' ||
      order.status === 'Partially Sold' ||
      (this.showSoldOrders && order.status === 'Sold')
    );

    // If showing all orders, return all filtered orders without date filtering
    if (this.showingAllOrders) {
      return filteredOrders;
    }

    // Initialize date range if not set
    if (!this.startDate || !this.endDate) {
      this.initializeDateRange();
    }

    // Apply date range filter based on the selected date filter type
    filteredOrders = filteredOrders.filter(order => {
      // For created date filter
      if (this.dateFilterType === 'created') {
        const orderDate = new Date(order.createdAt);
        const startDate = new Date(this.startDate!);
        const endDate = new Date(this.endDate!);
        endDate.setHours(23, 59, 59, 999); // Include the entire end day
        return orderDate >= startDate && orderDate <= endDate;
      }
      // For completed date filter
      else if (this.dateFilterType === 'completed') {
        // Only apply completed date filter for orders that have an actualEndDate
        if (order.actualEndDate) {
          const orderDate = new Date(order.actualEndDate);
          const startDate = new Date(this.startDate!);
          const endDate = new Date(this.endDate!);
          endDate.setHours(23, 59, 59, 999); // Include the entire end day
          return orderDate >= startDate && orderDate <= endDate;
        }
        // If no completed date exists, don't include the order in the results
        return false;
      }
      // Default to created date if no filter type is set
      const orderDate = new Date(order.createdAt);
      const startDate = new Date(this.startDate || new Date());
      const endDate = new Date(this.endDate || new Date());
      endDate.setHours(23, 59, 59, 999); // Include the entire end day
      return orderDate >= startDate && orderDate <= endDate;
    });

    return filteredOrders;
  }

  showAlert = false;
  alertTitle = '';
  alertMessage = '';
  alertType: 'success' | 'error' = 'success';
  isLoading = true;
  showAddFactoryOrderModal = false;
  billOfMaterialForm: FormGroup;
  factoryOrderForm: FormGroup;
  products: any[] = [];
  productsWithBOMs: any[] = [];
  // Chart data properties
  categoryLabels: string[] = [];
  categoryData: number[] = [];
  rawMaterials: any[] = [];
  rawMaterialSearchText: string = '';
  rawMaterialPageSize: number = 25;
  rawMaterialCurrentPage: number = 1;

  get filteredRawMaterials(): any[] {
    if (!this.rawMaterials) {
      return [];
    }
    const search = (this.rawMaterialSearchText || '').toLowerCase().trim();
    if (!search) {
      return this.rawMaterials;
    }
    return this.rawMaterials.filter((item: any) => {
      const name = (item.itemName || '').toLowerCase();
      return name.includes(search);
    });
  }

  get rawMaterialTotalPages(): number {
    const length = this.filteredRawMaterials.length;
    const pages = Math.ceil(length / this.rawMaterialPageSize) || 1;
    return Math.max(1, pages);
  }

  get rawMaterialPages(): number[] {
    const total = this.rawMaterialTotalPages;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = this.rawMaterialCurrentPage - half;
    let end = this.rawMaterialCurrentPage + half;

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  get rawMaterialPaged(): any[] {
    const start = (this.rawMaterialCurrentPage - 1) * this.rawMaterialPageSize;
    const end = start + this.rawMaterialPageSize;
    return this.filteredRawMaterials.slice(start, end);
  }

  changeRawMaterialPage(page: number): void {
    const total = this.rawMaterialTotalPages;
    if (page < 1 || page > total) {
      return;
    }
    this.rawMaterialCurrentPage = page;
  }

  onRawMaterialSearchChange(): void {
    this.rawMaterialCurrentPage = 1;
  }

  // Pagination and search for BOM product dropdown (products without BOMs)
  bomProductSearchText: string = '';
  bomProductPageSize: number = 25;
  bomProductCurrentPage: number = 1;

  get filteredBomProducts(): ItemDTO[] {
    if (!this.productsWithoutBOMs) {
      return [];
    }

    // Ensure the currently selected product is always present, even if it already has a BOM
    const selectedId = this.billOfMaterialForm?.get('finishedGoodItemID')?.value as number | null;
    let baseList = this.productsWithoutBOMs;
    if (selectedId) {
      const alreadyIncluded = baseList.some(p => p.itemID === selectedId);
      if (!alreadyIncluded) {
        const selectedProduct = this.products?.find(p => p.itemID === selectedId) || this.inventory?.find(p => p.itemID === selectedId);
        if (selectedProduct) {
          baseList = [...baseList, selectedProduct];
        }
      }
    }

    const search = (this.bomProductSearchText || '').toLowerCase().trim();
    if (!search) {
      return baseList;
    }
    return baseList.filter((item: ItemDTO) => {
      const name = (item.itemName || '').toLowerCase();
      return name.includes(search);
    });
  }

  get bomProductTotalPages(): number {
    const length = this.filteredBomProducts.length;
    const pages = Math.ceil(length / this.bomProductPageSize) || 1;
    return Math.max(1, pages);
  }

  get bomProductPages(): number[] {
    const total = this.bomProductTotalPages;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = this.bomProductCurrentPage - half;
    let end = this.bomProductCurrentPage + half;

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  get bomProductPaged(): ItemDTO[] {
    const start = (this.bomProductCurrentPage - 1) * this.bomProductPageSize;
    const end = start + this.bomProductPageSize;
    return this.filteredBomProducts.slice(start, end);
  }

  changeBomProductPage(page: number): void {
    const total = this.bomProductTotalPages;
    if (page < 1 || page > total) {
      return;
    }
    this.bomProductCurrentPage = page;
  }

  onBomProductSearchChange(): void {
    this.bomProductCurrentPage = 1;
  }

  // State and helpers for BOM product custom dropdown
  bomProductDropdownOpen: boolean = false;
  bomProductDropdownStyles: { top: string; left: string; width: string } | null = null;

  getBomProductDisplay(itemId: number | null | undefined): string {
    if (!itemId) return '';
    const product = this.productsWithoutBOMs?.find(p => p.itemID === itemId) || this.products?.find(p => p.itemID === itemId) || this.inventory?.find(p => p.itemID === itemId);
    return product ? product.itemName : '';
  }

  toggleBomProductDropdown(triggerEl?: HTMLElement): void {
    if (this.isEditMode) {
      return;
    }
    const isOpening = !this.bomProductDropdownOpen;
    this.bomProductDropdownOpen = isOpening;
    if (isOpening && triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      this.bomProductDropdownStyles = {
        top: `${Math.round(rect.bottom + 4)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
      };
      // Reuse the same guard to skip the immediate outside-click close
      this.rawMaterialDropdownJustOpened = true;
      setTimeout(() => (this.rawMaterialDropdownJustOpened = false), 0);
    }
  }

  onBomProductToggleMouseDown(triggerEl: HTMLElement, event: MouseEvent): void {
    if (this.isEditMode) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toggleBomProductDropdown(triggerEl);
  }

  selectBomProduct(product: ItemDTO | null): void {
    if (this.isEditMode) {
      this.bomProductDropdownOpen = false;
      return;
    }
    const control = this.billOfMaterialForm?.get('finishedGoodItemID');
    control?.setValue(product ? product.itemID : null);
    this.bomProductDropdownOpen = false;
    this.bomProductSearchText = '';
    this.bomProductCurrentPage = 1;
  }

  // Track which row index has the raw material dropdown open
  rawMaterialDropdownOpenIndex: number | null = null;
  // Store fixed-position styles per row for the dropdown panel
  rawMaterialDropdownStyles: { [index: number]: { top: string; left: string; width: string } } = {};
  // Guard to ignore the very first outside click after opening
  private rawMaterialDropdownJustOpened = false;

  // Dedicated bar chart data properties
  barChartLabels: string[] = [];
  barChartValues: number[] = [];
  uoms: UOM[] = [];
  get dropdownStatuses() {
    return this.statuses.filter(status =>
     status !== 'Partially Sold'
    );
  }
  WorkflowStatuses(order?: ProductionOrders) {
    return this.statuses.filter(status => {
      // Always filter out these statuses
      if (status === 'Partially Sold' || status === 'Sold') {
        return false;
      }

      // If no order is provided, include all remaining statuses
      if (!order) {
        return true;
      }

      // Only include Pending if the order's current status is Pending
      if (status === 'Pending') {
        return order.status === 'Pending';
      }

      // Include all other statuses
      return true;
    });
  }
  getSanitizedStatuses(): string[] {
    return this.statuses.map((s) => this.sanitize(s));
  }
  sanitize(status: string): string {
    return status.replace(/\s+/g, '-');
  }
  getStatusColor(status: string): string {

    switch (status) {

      case 'Pending':
        return '#FFEBEE'; // Light red
      case 'In Progress':
        return '#FFF8E1'; // Light yellow
      case 'Quality Check':
        return '#E3F2FD'; // Light blue
      case 'Completed':
        return '#E8F5E9'; // Light green
      case 'Sold':
        return '#F3E5F5'; // Light purple
      case 'Partially Sold':
        return '#FFF3E0'; // Light orange
      default:
        return '#F5F5F5'; // Light gray
    }

  }
  baseColors: string[] = [
    '#007A8F',
    '#FF2E17',
    '#849798',
    '#B0213C',
    '#26284F',
    '#FFA726',
    '#66BB6A',
    '#42A5F5',
    '#AB47BC',
    '#FF7043',
  ];
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  billOfMaterials: BOMHeaders[] = [];
  billOfMaterialsComponents :BOMComponents[] = [];
  inventory: any[] = [];
  DoughNutData: any[] = [];
  users: any[] = [];
  groupedBillOfMaterials: GroupedBillOfMaterial[] = [];
  factoryOrders: ProductionOrders[] = [];
  loading = false;
  // All possible statuses
  statuses: ('Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold' | 'Partially Sold')[] = [
    'Pending',
    'In Progress',
    'Quality Check',
    'Completed',
    'Sold',
    'Partially Sold'
  ];
  ordersByStatus: { [key: string]: ProductionOrders[] } = {
    Pending: [],
    'In Progress': [],
    'Quality Check': [],
    Completed: [],
    Sold: [],
    'Partially Sold': []
  };

  availableBudgets: any[] = [];
  budgetAssignments: BudgetAssignment[] = [];
  selectedProductId: number | undefined;
  isSavingBudgets = false;
  productTotalCost: number = 0;
  categories: any[] = [];

    loadCategories(): void {
      const token = localStorage.getItem('authToken');
      const headers = new HttpHeaders({
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      });

      this.http.get<any[]>(`${environment.apiUrl}/api/Category/GetAll`, { headers })
        .subscribe({
          next: (categories) => {
            this.categories = categories;
          },
          error: (error) => console.error('Error loading categories:', error)
        });
    }
  getCategoryDisplayName(categoryId: number | null | undefined): string {
    if (categoryId === null || categoryId === undefined) {
      return 'N/A';
    }
    const category = this.categories.find((c) => c.categoryId === categoryId);
    return category ? category.categoryName : 'Unknown Category';
  }

  private prepareChartsData(): void {
    // Update pie chart data
    this.categoryLabels = this.dropdownStatuses;
    this.categoryData = this.dropdownStatuses.map(status => {
      const statusCount = this.factoryOrders.filter(order => order.status === status).length;
      return statusCount || 0;
    });

    // Update bar chart data
    this.updateBarChartData();

    this.donutData = {
      labels: this.dropdownStatuses,
      datasets: [
        {
          data: this.categoryData,
          backgroundColor: ['#ff6384', '#ffcd35', '#17a2b8', '#4de270', '#B0213C'],
          hoverOffset: 8,
          borderRadius: 8,
        },
      ],
    };
    this.donutOptions = {
      responsive: true,
      maintainAspectRatio: true,
      cutout: '80%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#6b7280',
            font: { size: 14 },
            usePointStyle: true,
          },
        },
        tooltip: {
          backgroundColor: '#1f2937',
          bodyColor: '#f9fafb',
          titleColor: '#f9fafb',
          borderWidth: 1,
        },
      },
    };
  }

  private updateBarChartData(): void {
    // Clear previous data
    this.barChartLabels = [];
    this.barChartValues = [];

    // Group raw materials by name and sum quantities
    const materialMap = new Map<string, number>();

    this.billOfMaterials.forEach(bom => {
      if (bom.components) {
        bom.components.forEach(component => {
          const materialName = this.getRawMaterialName(component.rawMaterialItemID);
          const currentQuantity = materialMap.get(materialName) || 0;
          materialMap.set(materialName, currentQuantity + component.quantityRequired);
        });
      }
    });

    // Convert map to arrays for the chart
    materialMap.forEach((quantity, materialName) => {
      this.barChartLabels.push(materialName);
      this.barChartValues.push(quantity);
    });
    console
    // Update chart reference if it exists

  }

  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#6b7280',
          font: { size: 14 },
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: 'Raw Material Usage',
        font: {
          size: 16,
          weight: '500'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: '#1f2937',
        bodyColor: '#f9fafb',
        titleColor: '#f9fafb',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Raw Materials',
          color: '#6b7280',
          font: {
            size: 12
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Quantity Required',
          color: '#6b7280',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
    },
  };

  public barChartType: ChartType = 'bar';

  public barChartData: ChartConfiguration['data'] = {
    labels: this.barChartLabels,
    datasets: [
      {
        data: this.barChartValues,
        label: 'Raw Materials',
        backgroundColor: this.baseColors,
        borderColor: '#1f2937',
        borderWidth: 1
      }
    ],
  };

  // Handle partial sale of finished goods
  startPartialSale(order: ProductionOrders, quantityToSell: number) {
    if (!quantityToSell || quantityToSell <= 0) {
      this.showCustomAlert('Error', 'Please enter a valid quantity', 'error');
      return;
    }

    const maxQuantity = order.quantityToProduce - (order.quantitySold || 0);
    if (quantityToSell > maxQuantity) {
      this.showCustomAlert('Error', `Cannot sell more than ${maxQuantity} units`, 'error');
      return;
    }

    // Set the order and quantity, then show the modal
    this.order = order;
    this.quantityToSell = quantityToSell;
    this.showSellModal = true;
  }

  // Handle the actual sale when confirmed in the modal
  confirmSell() {
    if (this.order && this.quantityToSell > 0) {
      this.consumeFinishedGoods(this.order, this.quantityToSell);
      this.showSellModal = false;
    }
  }

  // Handle consumption of finished goods
  consumeFinishedGoods(order: ProductionOrders, quantityToSell?: number) {
    const isPartialSale = quantityToSell !== undefined && quantityToSell < (order.quantityToProduce - (order.quantitySold || 0));

    // If no quantity specified, mark all as sold
    if (!quantityToSell) {
      quantityToSell = order.quantityToProduce - (order.quantitySold || 0);
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Calculate new quantities and status
    const newQuantitySold = (order.quantitySold || 0) + quantityToSell;
    const isFullySold = newQuantitySold >= order.quantityToProduce;

    // Update the order with new status and quantities
    const updatedOrder = {
      ...order,
      status: isFullySold ? 'Sold' : 'Partially Sold',
      quantitySold: newQuantitySold,
      actualEndDate: isFullySold ? new Date().toISOString() : order.actualEndDate
    };

    this.loading = true;


    let totalComponentCost = 0;
    for (const item of this.stockCheckResults) {
      const rawMaterial = this.rawMaterials.find(rm => rm.itemID === item.component.rawMaterialItemID);
      if (rawMaterial && rawMaterial.purchasePrice !== undefined) {
        const unitCost = rawMaterial.purchasePrice / rawMaterial.purchaseQuantity;
        const componentCost = item.requiredQty * unitCost;
        totalComponentCost += componentCost;
      } else {
// $&
      }

    }

          // Calculate cost per unit for the finished good
          const costPerUnit = totalComponentCost


    this.http.get<ItemDTO>(`${environment.apiUrl}/api/Items/GetById?ItemsId=${order.finishedGoodItemID}`, { headers })
    .pipe(
      switchMap(item => {
        // Update the production order
        return this.http.put<ProductionOrders>(
          `${environment.apiUrl}/api/ProductionOrders/Update?productionOrderId=${order.productionOrderID}`,
          updatedOrder,
          { headers }
        ).pipe(
          // Create inventory transaction

          switchMap(updated => {
            const transaction = {
              itemID: order.finishedGoodItemID,
              warehouseID: order.warehouseID || 1, // Default to 1 if not specified
              transactionType: 'Sale',
              quantityChange: -quantityToSell, // Negative for outgoing
              costPerUnit: costPerUnit,
              transactionDate: new Date().toISOString(),
              referenceType: 'ProductionOrder',
              referenceID: order.productionOrderID.toString(),
              userID: this.getUserIdFromToken(),
              notes: `Sold/Consumed from Production Order ${order.orderNumber}`,
              batchNumber: '',
              expiryDate: null,
              referenceTypeCode: 'SALE'
            };
// $&
            return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, transaction, { headers });
          })
        );
      })
    ).subscribe({
      next: () => {
        // Update local state with new quantities and status
        const index = this.factoryOrders.findIndex(o => o.productionOrderID === order.productionOrderID);
        if (index !== -1) {
          this.factoryOrders[index] = {
            ...this.factoryOrders[index],
            status: updatedOrder.status as 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold' | 'Partially Sold',
            quantitySold: updatedOrder.quantitySold,
            actualEndDate: updatedOrder.actualEndDate
          };

          // Update the pie chart and other UI elements
          this.updatePieChartData();
          this.updateOrderCounts();
          this.groupOrdersByStatus();
          this.cdr.detectChanges(); // Trigger change detection
        }
        this.loading = false;
        this.showCustomAlert('Success', `Order ${order.orderNumber} has been marked as consumed/sold`, 'success');
      },
      error: (error) => {
// $&
        this.loading = false;
        this.showCustomAlert('Error', `Failed to mark order as consumed: ${error.error?.message || 'Unknown error'}`, 'error');
      }
    });
  }

  // Show custom alert
  showCustomAlert(title: string, message: string, type: 'success' | 'error' = 'success') {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;

    // Auto-hide after 5 seconds
    // setTimeout(() => {
    //   this.showAlert = false;
    // }, 20000);
  }

  onAlertClosed() {
    this.showAlert = false;
  }

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private budgetService: BudgetService,
    private inventoryService: InventoryService,
    private itemStandardCostHistoryService: ItemStandardCostHistoryService
  ) {
    // Initialize forms first
    this.factoryOrderForm = this.fb.group({
      finishedGoodItemID: [null, Validators.required],
      quantityToProduce: [1, [Validators.required, Validators.min(1)]],
      plannedStartDate: [new Date().toISOString().split('T')[0], Validators.required],
      plannedEndDate: [new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], Validators.required],
      notes: ['']
    });

    // Initialize budget assignments as a FormArray
    this.budgetAssignments = [];

    // Subscribe to product selection changes
    this.factoryOrderForm.get('finishedGoodItemID')?.valueChanges.subscribe((productId: number) => {
      if (productId) {
        this.selectedProductId = productId;
        this.loadProductCost(productId);
        // Reset budget assignments when product changes
        this.budgetAssignments = [];
        // Add default assignment if budgets are available
        if (this.availableBudgets.length > 0) {
          this.addDefaultAssignment();
        }
      }
    });

    this.billOfMaterialForm = this.fb.group({
      finishedGoodItemID: [null, Validators.required],
      description: [''],
      isActive: [true],
      components: this.fb.array([], Validators.required)
    });

    // Add first component row by default
    this.addComponent();

    // Subscribe to form value changes for debugging after form is initialized
    this.billOfMaterialForm.valueChanges.subscribe(() => {
// $&
// $&
// $&

      // Log component array validation
      if (this.components) {
// $&
// $&
        this.components.controls.forEach((control, index) => {
// $&
// $&
        });
      }
    });
  }


  animationDurations: number[] = [];

  // --- Mobile handler methods ---

  onMobileSaveBom(payload: BomFormPayload): void {
    this.billOfMaterialForm.patchValue({
      finishedGoodItemID: payload.finishedGoodItemID,
      description: payload.description,
      isActive: payload.isActive,
    });
    while (this.components.length > 0) this.components.removeAt(0);
    payload.components.forEach(c => {
      this.components.push(this.fb.group({
        rawMaterialItemID: [c.rawMaterialItemID],
        quantityRequired: [c.quantityRequired],
        componentUOMID: [c.componentUOMID],
        scrapFactor: [c.scrapFactor],
        notes: [c.notes],
      }));
    });
    // Set showAddModal=true so toggleAddModal() (called inside createBOM/updateBOM) closes it cleanly
    this.showAddModal = true;
    if (payload.bomHeaderID) {
      this.isEditMode = true;
      this.editingBomHeaderID = payload.bomHeaderID;
      this.updateBOM();
    } else {
      this.isEditMode = false;
      this.editingBomHeaderID = null;
      this.createBOM();
    }
  }

  onMobileDeleteBom(bom: BOMHeaders): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
    this.http.delete(`${environment.apiUrl}/api/BOMHeaders/Delete?BOMHeadersId=${bom.bomHeaderID}`, { headers }).subscribe({
      next: () => {
        this.billOfMaterials = this.billOfMaterials.filter(b => b.bomHeaderID !== bom.bomHeaderID);
        this.showCustomAlert('Success', 'BOM deleted successfully', 'success');
      },
      error: () => this.showCustomAlert('Error', 'Failed to delete BOM', 'error'),
    });
  }

  onMobileSaveFactoryOrder(payload: FactoryOrderFormPayload): void {
    this.factoryOrderForm.patchValue({
      finishedGoodItemID: payload.finishedGoodItemID,
      quantityToProduce: payload.quantityToProduce,
      plannedStartDate: payload.plannedStartDate,
      plannedEndDate: payload.plannedEndDate,
      notes: payload.notes,
    });
    if (payload.finishedGoodItemID) this.selectedProductId = payload.finishedGoodItemID;
    this.budgetAssignments = payload.budgetAssignments ?? [];
    this.showAddFactoryOrderModal = true;

    if (payload.finishedGoodItemID && (payload.budgetAssignments?.length ?? 0) > 0) {
      const token = localStorage.getItem('authToken');
      const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
      this.budgetService.getProductCost(payload.finishedGoodItemID, headers).subscribe({
        next: (res: any) => {
          this.productTotalCost = res.totalCost || 0;
          this.budgetAssignments = payload.budgetAssignments ?? [];
          this.createFactoryOrder();
        },
        error: () => this.createFactoryOrder(),
      });
    } else {
      this.createFactoryOrder();
    }
  }

  onMobileSellOrder(event: { order: any; qty: number }): void {
    this.consumeFinishedGoods(event.order, event.qty);
  }

  onMobileUpdateOrderStatus(event: { order: ProductionOrders; newStatus: string }): void {
    this.updateOrderStatus(event.order, event.newStatus as any);
  }

  onMobileUpdateProgress(event: { order: any }): void {
    const order = event.order;
    this.updateProgress({ target: { value: order.completionPercentage } } as any, order);
  }

  // --- End mobile handler methods ---

  ngOnInit(): void {
    const JWT = localStorage.getItem('authToken');
    const decodedToken: any = jwtDecode(JWT!);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();
    this.animationDurations = Array.from(
      { length: 3 },
      () => Math.floor(Math.random() * (250 - 80 + 1)) + 80
    );
    this.authService.isTokenExpired().subscribe((expired) => {
      if (expired) {
        this.authService.logOut();
        throw new Error('Session expired');
      }
    });
    this.loadUOMs();
    this.loadData();
    this.loadUsers();
    this.loadOrders();
    this.loadBudgets(); // Load budgets on component initialization
    this.prepareChartsData();
    this.updateChartData();
    this.loadCategories();
  }

  loadUOMs() {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    this.http.get<UOM[]>(`${environment.apiUrl}/api/UnitOfMeasure/GetAll`, { headers })
      .pipe(
        catchError(error => {
// $&
          return of([]);
        })
      )
      .subscribe({
        next: (uoms) => {
          this.uoms = uoms;
// $&
        },
        error: (error) => {
// $&
        }
      });
  }

  toggleAddModal() {
    this.showAddModal = !this.showAddModal;
    if (!this.showAddModal) {
      this.isEditMode = false;
      this.editingBomHeaderID = null;
      this.billOfMaterialForm.reset({
        finishedGoodItemID: null,
        description: '',
        isActive: true,
        components: []
      });
      // Remove all component rows
      while (this.components.length > 0) {
        this.components.removeAt(0);
      }
      this.addComponent();
    }
  }

  toggleAddFactoryOrderModal() {
    this.showAddFactoryOrderModal = !this.showAddFactoryOrderModal;
    if (this.showAddFactoryOrderModal) {
      this.factoryOrderForm.reset({
        quantityToProduce: 1,
        plannedStartDate: new Date().toISOString().split('T')[0],
        plannedEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: ''
      });
    }
  }


  private getUserIdFromToken(): number {
    const token = localStorage.getItem('authToken');
    if (!token) return 1; // Default user ID if no token

    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      return tokenPayload.userId || 1;
    } catch (e) {
// $&
      return 1;
    }
  }

  async createFactoryOrder() {
// $&

    const snapshotBudgetAssignments = [...this.budgetAssignments];

    if (this.factoryOrderForm.invalid) {
      this.factoryOrderForm.markAllAsTouched();
      this.showCustomAlert('Error', 'Please fill in all required fields (product, quantity, and dates) before saving.', 'error');
      return;
    }

    this.loading = true;
// $&

    const token = localStorage.getItem('authToken');
// $&

    if (!token) {
// $&
      this.loading = false;
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    try {
      // 1. Get the selected product and order quantity
// $&
      const selectedProductId = this.factoryOrderForm.get('finishedGoodItemID')?.value;
      const quantityToProduce = this.factoryOrderForm.get('quantityToProduce')?.value;
// $&

      const selectedProductIdNum = Number(selectedProductId);
      const selectedProduct = this.inventory.find((item: ItemDTO) => item.itemID === selectedProductIdNum);

      if (!selectedProduct) {
        console.error(`[Factory Order] Selected product (ID: ${selectedProductIdNum}) not found in inventory`);
        this.loading = false;
        this.showCustomAlert('Error', 'Selected product was not found in inventory. Please refresh and try again.', 'error');
        return;
      }
      // 2. Get the active BOM for this product
// $&
      const productBOM = this.billOfMaterials.find(bom =>
        bom.finishedGoodItemID === selectedProductIdNum && bom.isActive
      );

      if (!productBOM) {
        this.loading = false;
        this.showCustomAlert('Error', 'No active Bill of Materials found for this product. Please set up an active BOM before creating a production order.', 'error');
        return;
      }
      // 3. Get all BOM components and filter by the current BOM header ID
// $&
      const allBomComponents = await this.http.get<BOMComponents[]>(
        `${environment.apiUrl}/api/BOMComponents/GetAll`,
        { headers }
      ).toPromise();

      if (!allBomComponents || allBomComponents.length === 0) {
        this.loading = false;
        this.showCustomAlert('Error', 'Could not load BOM components. Please try again.', 'error');
        return;
      }

      // Filter components for the current BOM header
      const bomComponents = allBomComponents.filter(comp => comp.bomHeaderID === productBOM.bomHeaderID);

      if (bomComponents.length === 0) {
        this.loading = false;
        this.showCustomAlert('Error', 'This BOM has no components defined. Please add components to the BOM before creating a production order.', 'error');
        return;
      }

// $&

      // 4. Get current stock levels from the API
// $&
      let stockLevels: { [key: number]: number } = {};

      try {
        const response = await this.http.get<{ [key: string]: number }>(
          `${environment.apiUrl}/api/Items/GetAllStockLevels`,
          { headers }
        ).toPromise();

        if (!response) {
          throw new Error('No response from stock levels API');
        }

        // Convert string keys to numbers
        stockLevels = Object.fromEntries(
          Object.entries(response).map(([key, value]) => [parseInt(key), value])
        );

// $&
      } catch (error) {
// $&
        this.loading = false;
        return;
      }

      // 5. Check stock availability for each component
// $&
      const stockCheckResults = bomComponents.map((component: BOMComponents) => {
        // Convert BOM quantity from the component's UOM to the inventory base UOM
        // e.g., 18g -> 0.018kg when inventory is tracked in kg
        const convertedQtyPerUnit = this.convertToInventoryUOM(
          component.quantityRequired,
          component.componentUOMID,
          component.rawMaterialItemID
        );
        const requiredQty = convertedQtyPerUnit * quantityToProduce;
// $&

        // Get available stock from the stock levels
        const availableStock = stockLevels[component.rawMaterialItemID] || 0;
        const hasSufficientStock = availableStock >= requiredQty;
        return {
          component,
          requiredQty,
          availableStock,
          hasSufficientStock
        };
      });

      // 6. Check for any components with insufficient stock
// $&
      const insufficientStockItems = stockCheckResults.filter(item => !item.hasSufficientStock);

      if (insufficientStockItems.length > 0) {
// $&

        // Show error message with details of insufficient items
        const errorMessage = `Insufficient stock for the following items:\n` +
          insufficientStockItems.map(item =>
            `- ${this.getRawMaterialName(item.component.rawMaterialItemID)}: ` +
            `Required ${item.requiredQty}, Available ${item.availableStock}`
          ).join('\n');

// $&
        this.showCustomAlert('Error', errorMessage, 'error');
        this.loading = false;
        return;
      }

// $&

      // 6. Validate and save budget assignments before creating the order
      // Restore snapshot: async operations above (HTTP calls) can trigger Angular CD,
      // which causes the desktop budget selector to emit [] and reset this.budgetAssignments.
      this.budgetAssignments = snapshotBudgetAssignments;

      if (this.availableBudgets.length === 0) {
        this.showCustomAlert('Error', 'No budgets available. Please create budgets first.', 'error');
        this.loading = false;
        return;
      }

      if (!this.isBudgetAssignmentValid()) {
        this.showCustomAlert('Error', 'Budget assignments are invalid. Ensure the total assigned equals the total product cost.', 'error');
        this.loading = false;
        return;
      }

      const budgetsSaved = await this.saveBudgetAssignments(this.budgetAssignments);
      if (!budgetsSaved) {
        // Error already shown in saveBudgetAssignments
        this.loading = false;
        return;
      }

      // 7. Generate order number and prepare order data
// $&
      function formatDate(date: Date): string {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);  // Get last 2 digits of the year
        return `${year}${month}${day}`;
      }

      const orderNumber = `PO-${formatDate(new Date())}-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;

// $&

      const userId = this.getUserIdFromToken();
// $&
      const now = new Date().toISOString();
      const orderData = {
        productionOrderID: 0,
        orderNumber: orderNumber,
        finishedGoodItemID: selectedProductIdNum,
        quantityToProduce: this.factoryOrderForm.get('quantityToProduce')?.value,
        uomid: selectedProduct.purchaseUOMID, // Make sure this matches the backend's expected field name case
        bOMHeaderIDUsed: productBOM.bomHeaderID,
        status: 'Pending',
        warehouseID: 1, // Default warehouse, should be selected from a list
        plannedStartDate: new Date(this.factoryOrderForm.get('plannedStartDate')?.value).toISOString(),
        plannedEndDate: new Date(this.factoryOrderForm.get('plannedEndDate')?.value).toISOString(),
        actualStartDate: now, // Set to current time or null if not started
        actualEndDate: null, // Will be set when order is completed
        actualDirectLabourHours: 0, // Initialize to 0
        actualMachineHours: 0, // Initialize to 0
        createdAt: now,
        userIDCreated: userId,
        notes: this.factoryOrderForm.get('notes')?.value || '',
        completionPercentage: 0 // Initialize to 0%
      };
      // 8. Create the production order
// $&
      const orderResponse: any = await this.http.post(
        `${environment.apiUrl}/api/ProductionOrders/Add`,
        orderData,
        { headers }
      ).toPromise();

      const productionOrderID = orderResponse?.productionOrderID;
// $&

      if (!productionOrderID) {
        const errorMsg = 'Failed to create production order: No order ID returned';
// $&
        throw new Error(errorMsg);
      }

      // 9. Create inventory transactions for each component
      const transactionPromises = stockCheckResults.map(async (item, index) => {

          const rawMaterial = this.rawMaterials.find(rm => rm.itemID === item.component.rawMaterialItemID);
          // Safely derive IDs and pricing to avoid undefined access
          const rmItemId = rawMaterial?.itemID ?? item.component.rawMaterialItemID;
          const currentQuantity = rawMaterial?.quantity ?? 0;
          const purchasePrice = (rawMaterial && typeof rawMaterial.purchasePrice === 'number') ? rawMaterial.purchasePrice : 0;
          // Replace the current unit cost calculation with this
// Use the most recent transaction cost for that item or fall back to calculated value
const recentTransaction = this.inventoryTransactions
  .filter(t => t.itemID === rmItemId && t.costPerUnit > 0)
  .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];

let unitCost = 0;
// First try to use the most recent transaction with a valid cost
if (recentTransaction && recentTransaction.costPerUnit > 0) {
  unitCost = recentTransaction.costPerUnit;
}
// If no transaction history, calculate from purchase price/quantity
else if (rawMaterial && typeof rawMaterial.purchasePrice === 'number' &&
         typeof rawMaterial.purchaseQuantity === 'number' && rawMaterial.purchaseQuantity > 0) {
  unitCost = rawMaterial.purchasePrice / rawMaterial.purchaseQuantity;
}
// Last resort: use target cost
else if (rawMaterial && typeof rawMaterial.targetCost === 'number') {
  unitCost = rawMaterial.targetCost;
}
          // const unitCost = (currentQuantity > 0 && purchasePrice > 0) ? (purchasePrice / currentQuantity) : 0;
         // Format the transaction data according to the API spec
         const transactionPayload = {
           itemID: item.component.rawMaterialItemID,
           warehouseID: 1, // Default warehouse ID, update as needed
           transactionType: 'Production Consumption',
           quantityChange: -item.requiredQty, // Negative for consumption
           costPerUnit: unitCost, // Update with actual cost if available
           transactionDate: new Date().toISOString(),
           referenceType: 'ProductionOrder',
           referenceID: productionOrderID.toString(),
           userID: this.getUserIdFromToken(),
           notes: `Consumed for Production Order ${productionOrderID}`,
           batchNumber: '', // Add batch number if applicable
           expiryDate: null, // Add expiry date if applicable
           referenceTypeCode: 'PROD_ORD' // Standard reference type code for production orders
         };

// $&
        return this.http.post(
          `${environment.apiUrl}/api/InventoryTransactions/Add`,
          transactionPayload,
          { headers }
        ).toPromise().then(response => {
// $&
          return response;
        }).catch(error => {
// $&
          throw error; // Re-throw to be caught by the outer catch
        });
      });

      // Wait for all transactions to complete
// $&
      await Promise.all(transactionPromises);
// $&

      // 10. Calculate total cost of components

      // Calculate total cost of components (null-safe)
      let totalComponentCost = 0;
      for (const item of stockCheckResults) {
        const rawMaterial = this.rawMaterials.find(rm => rm.itemID === item.component.rawMaterialItemID);
        const rmItemId = rawMaterial?.itemID ?? item.component.rawMaterialItemID;
        const purchasePrice = (rawMaterial && typeof rawMaterial.purchasePrice === 'number') ? rawMaterial.purchasePrice : 0;
        const currentQuantity = this.calculateItemQuantity(rmItemId, this.inventoryTransactions);
        const unitCost = (currentQuantity > 0 && purchasePrice > 0) ? (purchasePrice / currentQuantity) : 0;
        const componentCost = item.requiredQty * unitCost;
        totalComponentCost += componentCost;

        const rmName = rawMaterial?.itemName || `Item ${rmItemId}`;
      }

      // Calculate cost per unit for the finished good
      const costPerUnit = totalComponentCost / quantityToProduce;
// $&

      // 11. Create a transaction for the finished good
      const finishedGoodTransaction = {
        itemID: selectedProductIdNum,
        warehouseID: 1, // Assuming default warehouse ID 1
        transactionType: 'Production',
        quantityChange: quantityToProduce, // Positive for finished good
        costPerUnit: costPerUnit, // Use calculated cost per unit
        transactionDate: new Date().toISOString(),
        referenceType: 'ProductionOrder',
        referenceID: productionOrderID.toString(),
        userID: this.getUserIdFromToken(),
        notes: `Produced from Production Order ${orderNumber}`,
        batchNumber: '', // Add batch number if applicable
        expiryDate: null, // Add expiry date if applicable
        referenceTypeCode: 'PROD_ORD'
      };

// $&
      await this.http.post(
        `${environment.apiUrl}/api/InventoryTransactions/Add`,
        finishedGoodTransaction,
        { headers }
      ).toPromise();

      // 11. Refresh data and show success
      await Promise.all([
        this.loadOrders(),
        this.updateChartData(),
        this.loadData() // Refresh inventory levels
      ]);

// $&
      this.toggleAddFactoryOrderModal();
      this.loading = false;

      // Show success message
      this.showCustomAlert('Success', `Production order ${orderNumber} created successfully`, 'success');
// $&

    } catch (error: any) {
      console.error('[Factory Order] Error in createFactoryOrder:', {
        message: error.message,
        status: error.status,
        url: error.url,
        error: error.error,
        stack: error.stack
      });

      this.loading = false;
      this.updateStatistics();
      // Show error message to user
      const errorMessage = error.error?.message ||
                         error.message ||
                         'Failed to create production order. Please try again.';
// $&
      this.showCustomAlert('Error', errorMessage, 'error');
    }
  }


  GetInitialQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const transaction = transactions.find(t => t.itemID === itemID && t.quantityChange > 0);
    return transaction ? transaction.quantityChange : 0;
  }

  calculateItemQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const itemTransactions = transactions.filter(transaction => transaction.itemID === itemID);
    return itemTransactions.reduce((sum, transaction) => sum + transaction.quantityChange, 0);
  }
  private async loadData(): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

      // First, ensure factory orders are loaded
      if (!this.factoryOrders || this.factoryOrders.length === 0) {
        const orders = await this.http.get<any[]>(`${environment.apiUrl}/api/ProductionOrders/GetAll`, { headers }).toPromise();
        this.factoryOrders = orders || [];
      }

      // Then load other data
      const [inventory, bomHeader, bomComponents] = await Promise.all([
        this.http.get<ItemDTO[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers }).toPromise(),
        this.http.get<BOMHeaders[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, { headers }).toPromise(),
        this.http.get<BOMComponents[]>(`${environment.apiUrl}/api/BOMComponents/GetAll`, { headers }).toPromise()
      ]);

      this.inventory = inventory || [];
      this.billOfMaterialsComponents = bomComponents || [];

      this.billOfMaterials = (bomHeader || []).map(header => ({
        ...header,
        components: (bomComponents || []).filter(component => component.bomHeaderID === header.bomHeaderID)
      }));

// $&

      // All finished goods (for BOM creation)
      this.products = (inventory || []).filter(item => item.itemTypeID === 2 && item.isActive === true);

      // Debug log all finished goods
      const allFinishedGoods = (inventory || []).filter(item => item.itemTypeID === 2);
// $&

      // Debug log BOM headers
// $&

      // Debug log which items have BOMs
      const itemsWithBOMs = allFinishedGoods.filter(item =>
        (bomHeader || []).some(header => header.finishedGoodItemID === item.itemID)
      );
// $&

      // Finished goods with existing BOMs that are active
// Finished goods with active BOMs
this.GetActiveBOMs();

      this.productsWithoutBOMs = allFinishedGoods.filter(item => {
        const hasBOM = (bomHeader || []).some(header => header.finishedGoodItemID === item.itemID);
        return item.itemTypeID === 2 && item.isActive === true && !hasBOM;
      });
// $&
// $&

      this.rawMaterials = (inventory || []).filter(item => item.itemTypeID === 1 && item.isActive === true);
      this.isLoading = false;
      this.updateChartData();
    } catch (error) {
// $&
    }
  }

public GetActiveBOMs(): any {
  this.productsWithBOMs = this.inventory.filter(item => {
    const hasActiveBOM = (this.billOfMaterials || []).some(header =>
      header.finishedGoodItemID === item.itemID && header.isActive === true
    );
    return item.itemTypeID === 2 && item.isActive === true && hasActiveBOM;
  });

}

  private loadUsers(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http
      .get<UserDTO[]>(`${environment.apiUrl}/api/Users/GetAll`, { headers })
      .subscribe({
        next: (users) => {
          this.users = users;
          this.loadOrders();
        },
        error: (error) => {
// $&
        },
      });
  }

  getUsername(userId: number): string {
    const user = this.users.find((u) => u.userId === userId);
    return user ? user.email : 'Unknown User';
  }

  totalOrders: number = 0;
  totalPendingOrders: number = 0;
  totalCompletedTodayOrders: number = 0;
  totalCost: number = 0;

  private loadOrders(): Promise<void> {
// $&
    this.updatePieChartData();
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return new Promise<void>((resolve, reject) => {
    // First, fetch all production orders
    this.http
      .get<ProductionOrders[]>(
        `${environment.apiUrl}/api/ProductionOrders/GetAll`,
        { headers }
      )
      .pipe(
        switchMap(orders => {
// $&
          if (!orders || orders.length === 0) {
// $&
            return of({ orders: [], transactions: [] });
          }

          // First, get all inventory transactions
// $&

          return this.http.get<any[]>(
            `${environment.apiUrl}/api/InventoryTransactions/GetAll`,
            { headers }
          ).pipe(
            map(transactions => {
// $&
              this.inventoryTransactions = transactions;
// $&
              // Filter transactions to only include sales for our production orders
              const orderIds = orders.map(o => o.productionOrderID);
              return transactions.filter(tx =>
                tx.transactionType === 'Sale' &&
                tx.referenceType === 'ProductionOrder' &&
                orderIds.includes(parseInt(tx.referenceID, 10))
              );
            }),
            catchError(error => {
// $&
// $&
              return of([]);
            }),
            map(transactions => {
// $&
              return {
                orders,
                transactions: transactions || []
              };
            })
          );
        })
      )
      .subscribe({
        next: ({ orders, transactions }) => {
// $&

          // Create a map of orderId to total sold quantity
          const orderSoldQuantities = new Map<number, number>();

// $&

          // Calculate total sold quantity for each order from transactions
          transactions.forEach(transaction => {
// $&
            if (transaction.transactionType === 'Sale' && transaction.quantityChange < 0) {
              const orderId = parseInt(transaction.referenceID, 10);
              if (!isNaN(orderId)) {
                const soldQty = Math.abs(transaction.quantityChange);
                const currentTotal = orderSoldQuantities.get(orderId) || 0;
                orderSoldQuantities.set(orderId, currentTotal + soldQty);
// $&
              }
            }
          });

          const today = new Date().toISOString().split('T')[0];

          this.factoryOrders = orders.map((order) => {
            // Use the calculated sold quantity from transactions, or fall back to the existing quantitySold
            const soldQty = orderSoldQuantities.get(order.productionOrderID) ?? (order.quantitySold || 0);
            const isFullySold = soldQty >= order.quantityToProduce;

            // Only update status if we have sales, otherwise keep the original status
            const status = soldQty > 0
              ? (isFullySold ? 'Sold' : 'Partially Sold')
              : order.status;

            const updatedOrder = {
              ...order,
              productName: this.getProductName(order.finishedGoodItemID),
              quantitySold: soldQty,
              status: status,
              completionPercentage: order.completionPercentage || 0,
              actualEndDate: isFullySold && !order.actualEndDate
                ? new Date().toISOString()
                : order.actualEndDate
            };
            return updatedOrder;
          });

// $&

          this.updateOrderCounts();
          this.groupOrdersByStatus();
          this.updatePieChartData();
          this.prepareChartsData();
          this.cdr.detectChanges();

// $&
          resolve();
        },
        error: (error) => {
// $&
          reject(error);
        },
      });
    });
  }
  updateOrderCounts() {
    const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
    this.totalOrders = this.factoryOrders.filter(order => order.status !== 'Sold').length;
    this.totalPendingOrders = this.factoryOrders.filter(o => o.status === 'Pending').length;
    this.totalCompletedTodayOrders = this.factoryOrders.filter(
      o => (o.status === 'Completed' || o.status === 'Sold' || o.status === 'Partially Sold') && o.actualEndDate?.startsWith(today)
    ).length;
  }
  toggleShowCompletedOrders(): void {
    this.showCompletedOrders = !this.showCompletedOrders;
    // No need to reload orders, just update the view
    this.updateOrderCounts();
    this.cdr.detectChanges();
  }

  onDateFilterChange(event: any): void {
    const dateValue = event.target.value;
    this.selectedDateFilterString = dateValue;
    if (dateValue) {
      this.selectedDateFilter = new Date(dateValue);
    } else {
      this.selectedDateFilter = null;
    }
    this.cdr.detectChanges();
  }

  onDateFilterTypeChange(event: any): void {
    this.cdr.detectChanges();
  }

  onDateRangeChange(event: any): void {
    this.cdr.detectChanges();
  }

  showAllOrders(): void {
    this.showingAllOrders = !this.showingAllOrders;
    // Reset date filter when showing all orders
    if (this.showingAllOrders) {
      // When showing all orders, we don't reset the date filter
      // The date filter will be hidden but retain its value
    } else {
      // When switching back to date filtering, reset to current week
      this.initializeDateRange();
    }
    this.cdr.detectChanges();
  }

  /**
   * Updates the status of a production order
   * @param order The order to update
   * @param newStatus The new status to set
   */
  // Define the order of status progression
  private readonly statusOrder = ['Pending', 'In Progress', 'Quality Check', 'Completed', 'Sold'];
  showPendingWarning = false;
  // Properties for Available Products table filters
  selectedDateFilter: Date | null = new Date();
  selectedDateFilterString: string | null = new Date().toISOString().split('T')[0];
  dateFilterType: 'created' | 'completed' = 'completed';
  showingAllOrders = false;
  startDate: string | null = null;
  endDate: string | null = null;

  // Initialize with current week range
  initializeDateRange(): void {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Adjust for Sunday

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    this.startDate = startOfWeek.toISOString().split('T')[0];
    this.endDate = endOfWeek.toISOString().split('T')[0];
  }

  updateOrderStatus(order: ProductionOrders, newStatus: 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold'): void {
    if (order.status === newStatus) return; // No change needed

    // Prevent changing status back to Pending if order has progressed beyond it
    const currentStatusIndex = this.statusOrder.indexOf(order.status);
    const newStatusIndex = this.statusOrder.indexOf(newStatus);

    if (newStatus === 'Pending' && currentStatusIndex > 0) {
      this.showCustomAlert(
        'Status Update Not Allowed',
        'Cannot change status back to Pending once the order has progressed beyond it.',
        'error'
      );
      return;
    }
    const previousStatus = order.status;
    // Update the order object directly
    order.status = newStatus;

    // Set ActualEndDate if status becomes 'Completed'
    if (newStatus === 'Completed' && !order.actualEndDate) {
      order.actualEndDate = new Date().toISOString();
    } else if (newStatus !== 'Completed') {
      order.actualEndDate = undefined;
    }
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Use the 'order' object directly as the payload, similar to onDrop
    this.http.put<ProductionOrders>(
      `${environment.apiUrl}/api/ProductionOrders/Update?productionOrderId=${order.productionOrderID}`,
      order, // Sending the entire 'order' object
      { headers }
    ).subscribe({
      next: (updatedOrder) => {
// $&

        // Then, add to status history
        const statusHistory: ProductionOrderStatusHistory = {
          statusHistoryID: 0, // Will be set by the server
          productionOrderID: order.productionOrderID,
          status: newStatus,
          statusChangedAt: new Date().toISOString(),
          userIDChanged: this.getUserIdFromToken(),
          notes: `Status changed from ${previousStatus} to ${newStatus}`
        };

        this.http.post<ProductionOrderStatusHistory>(
          `${environment.apiUrl}/api/ProductionOrderStatusHistory/Add`,
          statusHistory,
          { headers }
        ).subscribe({
          next: () => {

            this.updateStatistics();
            this.updatePieChartData();
            this.groupOrdersByStatus();
            this.cdr.detectChanges(); // Ensure UI is updated after successful history addition
          },
          error: (historyError) => {

            // Rollback the optimistic update if history update fails
            order.status = previousStatus;
            if (newStatus === 'Completed') order.actualEndDate = undefined; // Or revert to previous actualEndDate if it was set
            this.cdr.detectChanges();
          }
        });
      },
      error: (error) => {

        // Rollback the optimistic update
        order.status = previousStatus;
        if (newStatus === 'Completed') order.actualEndDate = undefined; // Or revert to previous actualEndDate
        this.cdr.detectChanges();
      }
    });
  }

  private updatePieChartData(): void {
    // Define the statuses we want to include in the chart
    const includedStatuses = ['Pending', 'In Progress', 'Quality Check', 'Completed'];
    this.updateOrderCounts();

    // Calculate counts only for included statuses
    const statusCount = includedStatuses.reduce((acc, status) => {
      acc[status] = this.factoryOrders.filter(
        (order) => order.status === status
      ).length;
      return acc;
    }, {} as Record<string, number>);

    // Create data array in the correct order
    const newData = includedStatuses.map(status => statusCount[status] || 0);

    this.DoughNutData = [...newData];
    this.categoryData = [...this.DoughNutData];

    // Update the pie chart colors (excluding Sold and Partially Sold)
    if (this.chart && this.chart.chart) {
      this.chart.chart.data.datasets[0].backgroundColor = [
        '#FF6384',  // Pending
        '#FFCD35',  // In Progress
        '#17A2B8',  // Quality Check
        '#4DE270'   // Completed
      ];

      // Update labels to only include included statuses
      this.chart.chart.data.labels = includedStatuses;
    }


    if (this.chart) {
      this.chart.update();
    }
  }



  private groupOrdersByStatus(): void {
    this.ordersByStatus = {
      Pending: [],
      'In Progress': [],
      'Quality Check': [],
      Completed: [],
      Sold: [],
      'Partially Sold': []
    };
    this.factoryOrders.forEach((order) => {
      if (this.ordersByStatus[order.status]) {
        this.ordersByStatus[order.status].push(order);
      }
    });
  }

  toggleExpansion(product: BOMHeaders): void {
    product.expanded = !product.expanded;
  }

  getRandomDuration(): number {
    return Math.floor(Math.random() * (250 - 80 + 1)) + 80;
  }
  currentPage: number = 0;
  itemsPerPage: number = 3;
  private fullProductNames: string[] = [];
  private fullDatasets: any[] = [];

  updateChartData(): void {
// $&

    // 1. Get unique product names (finished goods)
    const productNames = Array.from(
      new Set(this.billOfMaterials.map((b) => this.getProductName(b.finishedGoodItemID)))
    );
// $&

    // 2. Get unique raw material names from BOM components
    const rawMaterialNames = Array.from(
      new Set(
        this.billOfMaterials.flatMap((bomHeader) =>
          bomHeader.components
            ?.map((component) => this.getRawMaterialName(component.rawMaterialItemID))
            .filter(name => name !== 'Unknown Raw Material') // Filter out unknown names
        ).filter(Boolean) // Filter out any null/undefined values
      )
    );
// $&

    // 3. Create a map to store total raw material usage per product
    // Map structure: rawMaterialName -> (productName -> totalQuantity)
    const usageMap = new Map<string, Map<string, number>>();

    this.billOfMaterials.forEach((bomHeader) => {
      const product = this.getProductName(bomHeader.finishedGoodItemID);

      bomHeader.components?.forEach((component) => {
        const rawMaterial = this.getRawMaterialName(component.rawMaterialItemID);
        const quantityRequired = component.quantityRequired;

        if (!usageMap.has(rawMaterial)) {
          usageMap.set(rawMaterial, new Map<string, number>());
        }

        const productMap = usageMap.get(rawMaterial)!;
        productMap.set(
          product,
          (productMap.get(product) || 0) + quantityRequired
        );
      });
    });
// $&

    // 4. Prepare data for the chart datasets
    this.fullProductNames = productNames;
    this.fullDatasets = rawMaterialNames.map((rawMaterial, i) => {
      const color = this.baseColors[i % this.baseColors.length];

      const quantitiesPerProduct = productNames.map(
        (product) => usageMap.get(this.rawMaterials.find((rm) => rm.itemName === rawMaterial)?.itemName)?.get(product) || 0
      );

      return {
        label: rawMaterial,
        data: quantitiesPerProduct,
        backgroundColor: color,
        borderRadius: 4,
        borderWidth: 0,
      };
    });
// $&

    // 5. Apply pagination for chart display
    const start = this.currentPage * this.itemsPerPage;
    const end = start + this.itemsPerPage;

    const pagedLabels = this.fullProductNames.slice(start, end);
// $&
    const pagedDatasets = this.fullDatasets

      .map((dataset) => {
        const slicedData = dataset.data.slice(start, end);
        const hasNonZero = slicedData.some((qty: number) => qty > 0);

        return hasNonZero ? { ...dataset, data: slicedData } : null;
      })
      .filter((d): d is (typeof this.fullDatasets)[number] => d !== null);
// $&
    // 6. Update chart data and refresh
    this.barChartData = {
      labels: pagedLabels,
      datasets: pagedDatasets,
    };


  }
  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
     this.updateChartData();
    }
  }
  previousPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
       this.updateChartData();
    }
  }
  get totalPages(): number {
    return Math.ceil(this.fullProductNames.length / this.itemsPerPage);
  }

  onProgressInput(event: any, order: any) {
    order.completionPercentage = parseInt(event.target.value, 10);
  }

  updateProgress(event: any, order: any) {
// $&
    const newPercentage = parseInt(event.target.value, 10);
    // Ensure the order reflects the latest slider value before sending to the server
    order.completionPercentage = newPercentage;
    const apiUrl = `${environment.apiUrl}/api/ProductionOrders/Update?productionOrderId=${order.productionOrderID}`;
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
    this.http.put<ProductionOrders>(apiUrl, order, { headers }).subscribe({
      next: (updatedOrder) => {
        this.updateStatistics();
        this.updatePieChartData();
        this.prepareChartsData();
      },
      error: (error) => {
        console.error(
          'Error updating order completion percentage on the server:',
          error
        );
      },
    });
  }

  getProductName(finisihedGoodItemID: number): string {
    const product = this.products.find(
      (item) => item.itemID === finisihedGoodItemID
    );
    return product ? product.itemName : 'N/A';
  }
  getRawMaterialName(rawMaterialItemID: number): string {
    const rawMaterial = this.rawMaterials.find(
      (item) => item.itemID === rawMaterialItemID
    );

    return rawMaterial ? rawMaterial.itemName : 'N/A';
  }

  // Helper to display category name for a given raw material item
  getRawMaterialCategoryName(rawMaterialItemID: number): string {
    const raw = this.rawMaterials.find((item) => item.itemID === rawMaterialItemID);
    return this.getCategoryDisplayName(raw?.categoryID);
  }

  editProduct(bom: BOMHeaders): void {
    this.isEditMode = true;
    this.editingBomHeaderID = bom.bomHeaderID;
    // Reset form and remove all components
    this.billOfMaterialForm.reset({
      finishedGoodItemID: bom.finishedGoodItemID,
      description: bom.description,
      isActive: bom.isActive,
      components: []
    });
    while (this.components.length > 0) {
      this.components.removeAt(0);
    }
    // Add and patch each component
    if (bom.components && bom.components.length > 0) {
      bom.components.forEach(component => {
        const group = this.fb.group({
          rawMaterialItemID: [component.rawMaterialItemID, Validators.required],
          quantityRequired: [component.quantityRequired, [Validators.required, Validators.min(0.01)]],
          componentUOMID: [component.componentUOMID, Validators.required],
          scrapFactor: [component.scrapFactor, [Validators.min(0)]],
          notes: [component.notes]
        });
        this.components.push(group);
      });
    } else {
      this.addComponent();
    }
    this.showAddModal = true;
  }

  // Getter for easy access to form array
  get components() {
    return this.billOfMaterialForm.get('components') as FormArray<FormGroup>;
  }

  // Add a new component row to the form
  addComponent() {
    const componentGroup = this.fb.group({
      rawMaterialItemID: [null, Validators.required],
      quantityRequired: [1, [Validators.required, Validators.min(0.01)]],
      componentUOMID: [null, Validators.required],
      scrapFactor: [0, [Validators.min(0)]],
      notes: ['']
    });
    this.components.push(componentGroup);
  }

  /**
   * Calculate total BOM cost based on raw material costs and quantities
   */
  private calculateBOMCost(components: any[]): number {
    let totalBOMCost = 0;

    for (const component of components) {
      const rawMaterial = this.rawMaterials.find(rm => rm.itemID === component.rawMaterialItemID);

      if (rawMaterial) {
        // Calculate unit cost from purchase price and quantity
        let unitCost = 0;
        if (rawMaterial.purchasePrice && rawMaterial.purchaseQuantity && rawMaterial.purchaseQuantity > 0) {
          unitCost = rawMaterial.purchasePrice / rawMaterial.purchaseQuantity;
        } else if (rawMaterial.targetCost) {
          // Fall back to target cost if purchase data is not available
          unitCost = rawMaterial.targetCost;
        }

        // Convert BOM quantity from the component's UOM to the raw material's
        // purchase/base UOM before costing (e.g., 18g -> 0.018kg)
        const convertedQuantity = this.convertToInventoryUOM(
          component.quantityRequired,
          component.componentUOMID,
          component.rawMaterialItemID
        );

        // Calculate component cost including scrap factor
        const scrapFactor = component.scrapFactor || 0;
        const adjustedQuantity = convertedQuantity * (1 + scrapFactor / 100);
        const componentCost = adjustedQuantity * unitCost;

        totalBOMCost += componentCost;
      } else {
        console.warn(`[BOM Cost Calculation] Raw material not found for ID: ${component.rawMaterialItemID}`);
      }
    }
    return totalBOMCost;
  }

  /**
   * Save BOM cost to ItemStandardCostHistory if conditions are met
   */
  private saveBOMCostToHistory(finishedGoodItemID: number, bomCost: number): Observable<any> {
    return new Observable(observer => {
      // Check if we should save the BOM cost
      this.itemStandardCostHistoryService.shouldSaveBomCost(finishedGoodItemID, bomCost).subscribe({
        next: (shouldSave) => {
          if (shouldSave) {
            // Create the cost history record
            const costHistoryData: ItemStandardCostHistoryCreateRequest = {
              itemID: finishedGoodItemID,
              standardCost: bomCost,
              validFrom: new Date().toISOString(),
              validTo: new Date(2099, 11, 31).toISOString(), // Far future date
              userIDEntered: this.getUserIdFromToken()
            };

            this.itemStandardCostHistoryService.add(costHistoryData).subscribe({
              next: (result) => {
                observer.next(result);
                observer.complete();
              },
              error: (error) => {
                console.error('[BOM Cost] Error saving to ItemStandardCostHistory:', error);
                observer.error(error);
              }
            });
          } else {
            observer.next(null);
            observer.complete();
          }
        },
        error: (error) => {
          console.error('[BOM Cost] Error checking existing standard cost:', error);
          observer.error(error);
        }
      });
    });
  }

  // Remove a component row
  removeComponent(index: number) {
    this.components.removeAt(index);
  }

  // Unified form submission handler
  submitBOM() {
    if (this.billOfMaterialForm.invalid) {
      this.billOfMaterialForm.markAllAsTouched();
      return;
    }
    if (this.isEditMode && this.editingBomHeaderID) {
      this.updateBOM();
    } else {
      this.createBOM();
    }
  }

  // Helper method to increment version number
  private incrementVersion(version: string): string {
    if (!version) return '1.0';

    const parts = version.split('.').map(Number);
    if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) {
      return '1.0';
    }

    // Increment minor version by default
    return `${parts[0]}.${parts[1] + 1}`;
  }

  // Update an existing BOM
  updateBOM() {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Get current BOM to check existing version
    const currentBom = this.billOfMaterials.find(b => b.bomHeaderID === this.editingBomHeaderID);
    const currentVersion = currentBom ? currentBom.bomVersion : '1.0';
    const newVersion = this.incrementVersion(currentVersion);

    const formValue = this.billOfMaterialForm.value;
    const bomHeaderData = {
      bomHeaderID: this.editingBomHeaderID,
      finishedGoodItemID: Number(formValue.finishedGoodItemID),
      bomVersion: newVersion,
      description: formValue.description,

      isActive: formValue.isActive,
      userIDCreated: this.getUserIdFromToken()
    };
// $&
    this.loading = true;

    this.http.put(`${environment.apiUrl}/api/BOMHeaders/Update?BOMHeadersId=${this.editingBomHeaderID}`, bomHeaderData, { headers })
      .pipe(
        switchMap(() => {

          const existingComponents = this.billOfMaterialsComponents.filter(c => c.bomHeaderID === this.editingBomHeaderID);
          const deleteOps = existingComponents.map(c =>
            this.http.delete(`${environment.apiUrl}/api/BOMComponents/Delete?BOMComponentsId=${c.bomComponentID}`, { headers })
          );
          return deleteOps.length ? forkJoin(deleteOps) : of(null);
        }),
        switchMap(() => {

          const addOps = formValue.components.map((component: any) => {
            const componentData = {
              bomHeaderID: this.editingBomHeaderID,
              rawMaterialItemID: Number(component.rawMaterialItemID),
              quantityRequired: Number(component.quantityRequired),
              componentUOMID: Number(component.componentUOMID),
              scrapFactor: Number(component.scrapFactor),
              notes: component.notes
            };
            return this.http.post(`${environment.apiUrl}/api/BOMComponents/Add`, componentData, { headers });
          });
          return addOps.length ? forkJoin(addOps) : of(null);
        })
      )
      .subscribe({
        next: () => {
          // Recalculate BOM cost with UOM conversion and save to cost history
          const bomCost = this.calculateBOMCost(formValue.components);
          const finishedGoodItemID = Number(formValue.finishedGoodItemID);

          this.saveBOMCostToHistory(finishedGoodItemID, bomCost).subscribe({
            next: (costHistoryResult) => {
              if (costHistoryResult) {
                this.showCustomAlert('Success', `BOM updated successfully with cost ${bomCost.toFixed(2)} saved to cost history`, 'success');
              } else {
                this.showCustomAlert('Success', `BOM updated successfully. Cost ${bomCost.toFixed(2)} not saved (existing standard cost is higher)`, 'success');
              }
            },
            error: (costError) => {
              console.error('[BOM Update] Error saving BOM cost to history:', costError);
              this.showCustomAlert('Warning', `BOM updated successfully but failed to save cost history: ${costError.message || 'Unknown error'}`, 'error');
            },
            complete: () => {
              this.loadData();
              this.toggleAddModal();
              this.loading = false;
              this.showAlert = true;
              setTimeout(() => this.showAlert = false, 3000);
            }
          });
        },
        error: (error: any) => {
// $&
          this.loading = false;
          this.showCustomAlert('Error', `Error updating BOM: ${error.message || 'Please try again.'}`, 'error');
        }
      });
  }


  createBOM() {
    if (this.billOfMaterialForm.invalid) {
      this.billOfMaterialForm.markAllAsTouched();
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    const formValue = this.billOfMaterialForm.value;

    const bomHeaderData = {
      finishedGoodItemID: Number(formValue.finishedGoodItemID),
      bomVersion: '1.0', // Start with version 1.0 for new BOMs
      description: formValue.description,

      isActive: formValue.isActive,
      userIDCreated: this.getUserIdFromToken()
    };

    this.loading = true;


    this.http.post<{bomHeaderID: number}>(`${environment.apiUrl}/api/BOMHeaders/Add`, bomHeaderData, { headers })
      .pipe(
        switchMap((response) => {
          const bomHeaderID = response.bomHeaderID;
// $&

          const componentRequests = formValue.components.map((component: any) => {
            const componentData = {
              bomHeaderID: bomHeaderID,
              rawMaterialItemID: Number(component.rawMaterialItemID),
              quantityRequired: Number(component.quantityRequired),
              componentUOMID: Number(component.componentUOMID),
              scrapFactor: component.scrapFactor ? Number(component.scrapFactor) : 0,
              notes: component.notes || ''
            };

            return this.http.post(`${environment.apiUrl}/api/BOMComponents/Add`, componentData, { headers });
          });

          return forkJoin(componentRequests).pipe(
            map(() => bomHeaderID)
          );
        })
      )
      .subscribe({
        next: (bomHeaderID) => {
          // Calculate BOM cost and save to ItemStandardCostHistory
          const bomCost = this.calculateBOMCost(formValue.components);
          const finishedGoodItemID = Number(formValue.finishedGoodItemID);

          // Save BOM cost to history (if conditions are met)
          this.saveBOMCostToHistory(finishedGoodItemID, bomCost).subscribe({
            next: (costHistoryResult) => {
              if (costHistoryResult) {
                this.showCustomAlert('Success', `BOM created successfully with cost ${bomCost.toFixed(2)} saved to cost history`, 'success');
              } else {
                this.showCustomAlert('Success', `BOM created successfully. Cost ${bomCost.toFixed(2)} not saved (existing standard cost is higher)`, 'success');
              }
            },
            error: (costError) => {
              console.error('[BOM Creation] Error saving BOM cost to history:', costError);
              this.showCustomAlert('Warning', `BOM created successfully but failed to save cost history: ${costError.message || 'Unknown error'}`, 'error');
            },
            complete: () => {
              this.loadData();
              this.toggleAddModal();
              this.loading = false;
            }
          });
        },
        error: (error: any) => {
          console.error('Error creating BOM:', error);
          this.loading = false;
          this.showCustomAlert('Error', `Error creating BOM: ${error.message || 'Please try again.'}`, 'error');
        }
      });
  }

  onDrop(
    event: CdkDragDrop<ProductionOrders[]>,
    newStatus: 'Pending' | 'In Progress' | 'Quality Check' | 'Completed' | 'Sold' | 'Partially Sold'
  ): void {
    // Prevent moving items back to Pending status
    const order = event.previousContainer.data[event.previousIndex];
    const currentStatusIndex = this.statusOrder.indexOf(order.status);

    if (newStatus === 'Pending' && currentStatusIndex > 0) {
      this.showPendingWarning = true;
      setTimeout(() => {
        this.showPendingWarning = false;
        this.showAlert = false;
        this.cdr.detectChanges();
      }, 3000);
      this.showCustomAlert(
        'Status Update Not Allowed',
        'Cannot move order back to Pending status once it has progressed beyond it.',
        'error'
      );
      return;
    }

    this.updatePieChartData();
    this.updateChartData();
    this.prepareChartsData();

    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      const order = event.previousContainer.data[event.previousIndex];
// $&
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      order.status = newStatus;
      if (newStatus === 'Completed') {
        order.actualEndDate = new Date().toISOString();
      } else {
        order.actualEndDate = undefined;
      }
      const apiUrl = `${environment.apiUrl}/api/ProductionOrders/Update?productionOrderId=${order.productionOrderID}`;
      const token = localStorage.getItem('authToken');
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      this.http.put<ProductionOrdersExtended>(apiUrl, order, { headers }).subscribe({
        next: (updatedOrder) => {
          this.updateStatistics();
        },
        error: (error) => {
// $&
        },
      });
      this.http.post<ProductionOrderStatusHistory>(`${environment.apiUrl}/api/ProductionOrderStatusHistory/Add`, {
        statusHistoryID: 0,
        productionOrderID: order.productionOrderID,
        status: newStatus,
        statusChangedAt: new Date().toISOString(),
        userIDChanged: 1,
        notes: 'Updated via Workflow Tracker',
      }, { headers }).subscribe({
        next: (updatedOrder) => {
          this.updateStatistics();
        },
        error: (error) => {
// $&
        },
      });
    }
    this.groupOrdersByStatus();
    this.updatePieChartData();
    this.prepareChartsData();
  }

  animatedPendingOrders: number = 0;
  private animateCountUp(finalValue: number): void {
    const duration = 1000;
    const frameRate = 30;
    const totalFrames = duration / (1000 / frameRate);
    let frame = 0;
    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      this.animatedPendingOrders = Math.round(finalValue * progress);
      if (frame >= totalFrames) {
        clearInterval(counter);
        this.animatedPendingOrders = finalValue;
      }
    }, 1000 / frameRate);
  }

  private updateStatistics(): void {
    const today = new Date().toISOString().split('T')[0];
    this.totalOrders = this.factoryOrders.length;
    const pending = this.factoryOrders.filter(
      (o) => o.status === 'Pending'
    ).length;
    this.totalPendingOrders = pending;
    this.animateCountUp(pending);
    this.totalCompletedTodayOrders = this.factoryOrders.filter(
      (o) => (o.status === 'Completed' || o.status === 'Sold' || o.status === 'Partially Sold') && o.actualEndDate?.startsWith(today)
    ).length;
  }

  async saveBudgetAssignments(assignments: BudgetAssignment[]): Promise<boolean> {
    if (!this.selectedProductId) {
      console.error('No product selected');
      return false;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      this.showCustomAlert('Error', 'Authentication required', 'error');
      return false;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Filter out invalid assignments
    const validAssignments = assignments.filter(a =>
      a && a.budgetID && (a.amount || 0) > 0
    );



    // Get the product name first to ensure we have it before mapping
    const productId = this.selectedProductId;
    const productName = this.getProductName(productId);
    const fiscalYear = new Date().getFullYear().toString();

    // Prepare the assignments with product info
    const preparedAssignments: BudgetAssignment[] = validAssignments.map(assignment => {
      // Find the budget to get the name and fiscal year if not provided
      const budget = this.availableBudgets.find(b =>
        b.budgetID === assignment.budgetID || b.budgetID === assignment.budgetID
      );

      const qty = this.factoryOrderForm.get('quantityToProduce')?.value || 1;
      const scaledTotal = (this.productTotalCost || 0) * qty;

      return {
        budgetID: budget?.budgetID || budget?.budgetID || assignment.budgetID,
        amount: parseFloat(assignment.amount?.toString() || '0'),
        productId: productId,
        productName: productName || 'Unknown Product',
        totalCost: scaledTotal,
        budgetName: assignment.budgetName || budget?.budgetName || 'Unknown Budget',
        fiscalYear: assignment.fiscalYear || budget?.fiscalYear || fiscalYear
      };
    });
    this.isSavingBudgets = true;

    try {
      // Save assignments one by one
      for (const assignment of preparedAssignments) {
        try {
          const result = await lastValueFrom(
            this.budgetService.saveBudgetAssignments([assignment], headers)
          );
        } catch (error: any) {
          console.error('Error saving budget assignment:', error);
          const errorMessage = error?.error?.message ||
                             error?.message ||
                             `Failed to save budget assignment for ${assignment.budgetName}. Please try again.`;
          this.showCustomAlert('Error', errorMessage, 'error');
          this.isSavingBudgets = false;
          return false; // Stop saving if any assignment fails
        }
      }

      // If we get here, all assignments were saved successfully
      this.showCustomAlert('Success', 'All budget assignments saved successfully', 'success');
      return true;
    } catch (error: any) {
      console.error('Unexpected error saving budget assignments:', error);
      const errorMessage = error?.error?.message ||
                         error?.message ||
                         'An unexpected error occurred while saving budget assignments.';
      this.showCustomAlert('Error', errorMessage, 'error');
      return false;
    } finally {
      this.isSavingBudgets = false;
    }
  }

  private loadBudgets() {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    this.budgetService.getAllBudgets(headers).subscribe({
      next: (budgets: any[]) => {
        // Map the API response to match the expected format
        this.availableBudgets = budgets.map(budget => ({
          budgetID: budget.budgetID ?? budget.BudgetID ?? budget.budgetId,
          budgetName: budget.budgetName ?? budget.BudgetName,
          fiscalYear: (budget.fiscalYear ?? budget.FiscalYear ?? new Date().getFullYear()).toString(),
          budgetType: budget.budgetType ?? budget.BudgetType ?? 'Monthly',
          createdAt: budget.createdAt ?? budget.CreatedAt ?? null
        }));
        // Keep BudgetService's cache in sync so saveBudgetAssignments has access
        if (this.budgetService && Array.isArray(this.availableBudgets)) {
          this.budgetService.availableBudgets = [...this.availableBudgets];
        }

        // Do not auto-create default assignments; user must choose explicitly in the selector
      },
      error: (error) => {
        console.error('Failed to load budgets:', error);
        this.showCustomAlert('Error', 'Failed to load budgets. Please try again.', 'error');
      }
    });
  }

  private addDefaultAssignment() {
    if (this.availableBudgets.length > 0 && this.selectedProductId) {
      const qty = this.factoryOrderForm.get('quantityToProduce')?.value || 1;
      const scaledTotal = (this.productTotalCost || 0) * qty;
      this.budgetAssignments = [{
        budgetID: this.availableBudgets[0].budgetID,
        amount: scaledTotal,
        productId: this.selectedProductId,
        productName: this.getProductName(this.selectedProductId),
        totalCost: scaledTotal,
        budgetName: this.availableBudgets[0].budgetName,
        fiscalYear: this.availableBudgets[0].fiscalYear
      }];
    }
  }

  private loadProductCost(productId: number): void {
    if (!productId) {
      this.productTotalCost = 0;
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    this.loading = true;
    this.budgetService.getProductCost(productId, headers).subscribe({
      next: (response) => {
        this.productTotalCost = response.totalCost || 0;

        // Do not auto-update or auto-create budget assignments here; let the selector manage it

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading product cost:', error);

        // Check if this is a legacy product error
        if ((error as any).isLegacyProductError) {
          this.showCustomAlert(
            'Action Required',
            error.message,
            'error'
          );
        } else {
          this.showCustomAlert('Error', 'Failed to load product cost. Please try again.', 'error');
        }

        this.loading = false;
        this.productTotalCost = 0;
      }
    });
  }

  getUOMsForItem(itemId: string | number) {
    try {
      if (!itemId && itemId !== 0) return [];

      // If itemId is a string that can be parsed as a number, convert it
      const numericId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
      if (isNaN(numericId)) return [];

      // Find the raw material to get its UOM ID
      const material = this.rawMaterials.find(rm => rm.itemID === numericId);
      if (!material) return [];

      // Get the UOM ID from the raw material; fallback to purchaseUOMID if baseUOMID is not present
      const uomId = (material.baseUOMID ?? material.purchaseUOMID) as number | undefined;
      if (uomId === undefined || uomId === null) return [];

      const baseUom = this.uoms.find(uom => uom.uomid === uomId);
      if (!baseUom) return [];

      // Return UOMs compatible with the raw material (e.g., KG -> G)
      const baseFamily = this.getUOMFamily(baseUom);
      const compatible = this.uoms.filter(u => this.getUOMFamily(u) === baseFamily);

      // Always include the original UOM at the top
      const unique = [baseUom, ...compatible.filter(u => u.uomid !== baseUom.uomid)];
      return unique;
    } catch (error) {
      console.error('Error in getUOMsForItem:', error);
      return [];
    }
  }

  onAssignmentsChange(assignments: BudgetAssignment[]): void {
    try {
      this.budgetAssignments = Array.isArray(assignments) ? assignments : [];
      // Optionally trigger change detection if needed for UI updates
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error updating budget assignments from child:', error);
      this.budgetAssignments = [];
    }
  }

  isBudgetAssignmentValid(): boolean {
    // If no product is selected, no need to validate budget
    if (!this.factoryOrderForm.get('finishedGoodItemID')?.value) {
      return false;
    }

    // If no budget assignments but budgets are available, it's invalid
    if ((!this.budgetAssignments || this.budgetAssignments.length === 0) && this.availableBudgets.length > 0) {
      return false;
    }

    // If no budgets are available, it's invalid (user must create budgets first)
    if (this.availableBudgets.length === 0) {
      return false;
    }

    // Calculate total assigned amount
    const totalAssigned = this.budgetAssignments.reduce((sum, assignment) => {
      return sum + (Number(assignment.amount) || 0);
    }, 0);

    // Compare against scaled total cost = per-unit cost * quantity
    const qty = this.factoryOrderForm.get('quantityToProduce')?.value || 1;
    const scaledTotal = (this.productTotalCost || 0) * qty;
    // Check if the total assigned matches the scaled total cost (with small tolerance for floating point)
    const isValid = Math.abs(totalAssigned - scaledTotal) < 0.01;
    return isValid;
  }

  getUOMDisplay(component: any): { name: string, code: string | null, uomid: number | null } {
    const rawMaterialId = component.get('rawMaterialItemID')?.value;
    if (!rawMaterialId) return { name: 'N/A', code: null, uomid: null };

    const uoms = this.getUOMsForItem(rawMaterialId);
    if (uoms.length === 0) return { name: 'No UOM found', code: null, uomid: null };

    const selectedUomId = component.get('componentUOMID')?.value;
    const matched = uoms.find(u => u.uomid === selectedUomId) || uoms[0];

    if (!selectedUomId) {
      component.get('componentUOMID')?.setValue(matched.uomid);
      component.get('componentUOMID')?.updateValueAndValidity();
    }

    return {
      name: matched?.uomName || 'N/A',
      code: matched?.uomCode || null,
      uomid: matched?.uomid || null
    };
  }

  onRawMaterialChange(index: number) {
    const component = this.components.at(index);
    const rawMaterialId = component.get('rawMaterialItemID')?.value;

    if (rawMaterialId) {
      const item = this.rawMaterials.find(r => r.itemID === rawMaterialId);
      if (item) {
        // Get the UOM for the selected raw material
        const uoms = this.getUOMsForItem(item.itemID);
        if (uoms.length > 0) {
          // Set the componentUOMID to the first available compatible UOM
          const uomId = uoms[0].uomid;
          component.get('componentUOMID')?.setValue(uomId);
          // Ensure the control is enabled for validation
          component.get('componentUOMID')?.enable();
          // Mark the control as touched to trigger validation
          component.get('componentUOMID')?.markAsTouched();
          // Update validity
          component.updateValueAndValidity();

// $&
        } else {
// $&
        }
      }
      // Update the form's validity
      this.billOfMaterialForm.updateValueAndValidity();
      // Force update the form's status
      this.billOfMaterialForm.markAllAsTouched();
      // Trigger change detection
      this.cdr.detectChanges();

// $&
// $&
// $&
// $&
    }
  }

  /**
   * Returns a normalized conversion factor for a UOM code so that quantities
   * expressed in different units within the same family can be compared.
   * The factor is relative to the smallest unit in each family:
   *   weight: g=1, kg=1000, mg=0.001
   *   volume: ml=1, l=1000
   *   length: mm=1, cm=10, m=1000
   *   count:  each/unit/pcs = 1
   */
  private getConversionFactor(unitCode?: string | null): number {
    if (!unitCode) return 1;
    const normalized = unitCode.toLowerCase();

    const lookup: Record<string, number> = {
      kg: 1000,
      g: 1,
      mg: 0.001,
      gram: 1,
      kilogram: 1000,
      milligram: 0.001,
      l: 1000,
      lt: 1000,
      liter: 1000,
      litre: 1000,
      ml: 1,
      milliliter: 1,
      millilitre: 1,
      m: 1000,
      cm: 10,
      mm: 1,
      meter: 1000,
      metre: 1000,
      centimeter: 10,
      millimeter: 1,
      each: 1,
      ea: 1,
      unit: 1,
      pcs: 1,
      piece: 1,
      pc: 1,
    };

    return lookup[normalized] ?? 1;
  }

  /**
   * Converts a BOM component quantity from the component's UOM to the raw
   * material's inventory (base/purchase) UOM.
   *
   * Example: 18 g  -> 0.018 kg   (componentUOM=g, inventoryUOM=kg)
   *          500 ml -> 0.5 l      (componentUOM=ml, inventoryUOM=l)
   */
  private convertToInventoryUOM(
    quantity: number,
    componentUOMID: number,
    rawMaterialItemID: number
  ): number {
    // Look up the component UOM code
    const componentUom = this.uoms.find(u => u.uomid === componentUOMID);
    if (!componentUom) return quantity; // Can't convert without knowing the UOM

    // Look up the raw material's base/inventory UOM
    const material = this.rawMaterials.find((rm: any) => rm.itemID === rawMaterialItemID);
    if (!material) return quantity;

    const inventoryUomId = (material.baseUOMID ?? material.purchaseUOMID) as number | undefined;
    if (!inventoryUomId) return quantity;

    // If they're the same UOM, no conversion needed
    if (componentUOMID === inventoryUomId) return quantity;

    const inventoryUom = this.uoms.find(u => u.uomid === inventoryUomId);
    if (!inventoryUom) return quantity;

    const componentFactor = this.getConversionFactor(componentUom.uomCode);
    const inventoryFactor = this.getConversionFactor(inventoryUom.uomCode);

    // Avoid division by zero
    if (inventoryFactor === 0) return quantity;

    // Convert: quantity * (componentFactor / inventoryFactor)
    // e.g., 18g -> kg: 18 * (1 / 1000) = 0.018
    return quantity * (componentFactor / inventoryFactor);
  }

  /**
   * Map UOMs into compatibility families (weight, volume, length, count) using code/name heuristics
   * to allow sensible alternatives (e.g., KG <-> G)
   */
  private getUOMFamily(uom: UOM): 'weight' | 'volume' | 'length' | 'count' | 'other' {
    const code = (uom.uomCode || '').toLowerCase();
    const name = (uom.uomName || '').toLowerCase();

    const isWeight = ['kg', 'g', 'gram', 'kilogram', 'lb', 'pound', 'oz', 'ounce', 'mg'].some(k => code.includes(k) || name.includes(k));
    if (isWeight) return 'weight';

    const volumeCodeExact = ['l', 'lt', 'ml'];
    const volumeNameTokens = ['liter', 'litre', 'milliliter', 'millilitre', 'gallon'];

    const isVolume =
      volumeCodeExact.some(k => code === k) ||
      volumeNameTokens.some(k => code.includes(k) || name.includes(k));
    if (isVolume) return 'volume';

    const isLength = ['m', 'meter', 'metre', 'cm', 'centimeter', 'mm', 'millimeter', 'inch', 'ft', 'foot'].some(k => code.includes(k) || name.includes(k));
    if (isLength) return 'length';

    const isCount = ['ea', 'each', 'unit', 'pcs', 'piece', 'pc'].some(k => code.includes(k) || name.includes(k));
    if (isCount) return 'count';

    return 'other';
  }

  lightenColor(hex: string, percent: number): string {
    hex = hex.replace(/^#/, '');
    let num = parseInt(hex, 16);
    let r = (num >> 16) + (255 - (num >> 16)) * percent;
    let g = ((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * percent;
    let b = (num & 0x0000ff) + (255 - (num & 0x0000ff)) * percent;
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  }
}
